/**
 * AssetCard — one asset tile: thumbnail (preview byte-stream for `active`
 * assets, a status placeholder otherwise), dimensions, tags, and — unless
 * read-only — inline tag editing and soft-delete (behind a confirm dialog).
 *
 * Fully owner-agnostic: the caller supplies the preview URL and the
 * mutation callbacks, so the same card renders an arche's own assets, a
 * Workspace library, and the read-only "shared from workspaces" grid.
 */

import * as React from 'react';
import { Volume2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ContributorArcheAsset } from '@/lib/api';
import { formatBytes, isAudioContentType } from './format';

export interface AssetCardProps {
  asset: ContributorArcheAsset;
  /** Preview byte-stream URL; null renders the status placeholder. */
  previewUrl: string | null;
  /** Hides every mutation control (viewer role, shared assets, disabled library). */
  readOnly?: boolean;
  /** Shown under the name — e.g. the owning workspace's display name. */
  sourceLabel?: string;
  onDelete?: () => void;
  deleting?: boolean;
  onSaveTags?: (tags: string[]) => Promise<unknown>;
}

export function AssetCard({
  asset,
  previewUrl,
  readOnly = false,
  sourceLabel,
  onDelete,
  deleting = false,
  onSaveTags,
}: AssetCardProps) {
  const [editingTags, setEditingTags] = React.useState(false);
  const [tagDraft, setTagDraft] = React.useState(asset.tags.join(', '));
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [previewFailed, setPreviewFailed] = React.useState(false);

  // Keep the draft in sync when the asset's tags change underneath us
  // (e.g. a refetch after another save) — but never while the user is
  // actively editing.
  React.useEffect(() => {
    if (!editingTags) {
      setTagDraft(asset.tags.join(', '));
    }
  }, [asset.tags, editingTags]);

  // A new preview URL gets a fresh chance to load.
  React.useEffect(() => {
    setPreviewFailed(false);
  }, [previewUrl]);

  const canMutate = !readOnly && asset.status === 'active';
  const label = asset.name ?? asset.assetId;

  async function saveTags() {
    if (!onSaveTags) return;
    const tags = tagDraft
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveTags(tags);
      setEditingTags(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save tags');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="overflow-hidden rounded border">
      <div className="aspect-square bg-muted flex items-center justify-center">
        {isAudioContentType(asset.contentType) ? (
          <div className="flex w-full flex-col items-center gap-3 p-3">
            <Volume2 aria-hidden="true" className="h-10 w-10 text-muted-foreground" />
            {previewUrl && (
              <audio
                controls
                preload="none"
                src={previewUrl}
                className="w-full"
                aria-label={`Play ${label}`}
              />
            )}
          </div>
        ) : previewUrl && !previewFailed ? (
          <img
            src={previewUrl}
            alt={asset.altText ?? asset.name ?? asset.assetId}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setPreviewFailed(true)}
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            {previewFailed ? label : asset.status}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="truncate text-sm font-medium" title={label}>
          {label}
        </div>
        {sourceLabel && (
          <div className="truncate text-xs text-muted-foreground" title={sourceLabel}>
            from {sourceLabel}
          </div>
        )}
        <div className="mt-1 text-xs text-muted-foreground">
          {asset.widthPx && asset.heightPx
            ? `${asset.widthPx}×${asset.heightPx} · ${formatBytes(asset.sizeBytes)}`
            : `Sound · ${formatBytes(asset.sizeBytes)}`}
        </div>

        {editingTags ? (
          <div className="mt-2">
            <label className="sr-only" htmlFor={`asset-tags-${asset.assetId}`}>
              Tags for {label}
            </label>
            <input
              id={`asset-tags-${asset.assetId}`}
              type="text"
              className="w-full rounded border border-input bg-background p-1 text-xs"
              placeholder="pack1, premium"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              disabled={saving}
            />
            {saveError && (
              <div role="alert" className="mt-1 text-xs text-destructive">
                {saveError}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <Button size="sm" className="flex-1" onClick={saveTags} disabled={saving}>
                {saving ? 'Saving…' : 'Save tags'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setTagDraft(asset.tags.join(', '));
                  setSaveError(null);
                  setEditingTags(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          asset.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {asset.tags.map((t) => (
                <Badge key={t} variant="secondary">
                  {t}
                </Badge>
              ))}
            </div>
          )
        )}

        {canMutate && !editingTags && (
          <div className="mt-3 flex gap-2">
            {onSaveTags && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setEditingTags(true)}
                aria-label={`Edit tags for ${label}`}
              >
                Edit tags
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setConfirmingDelete(true)}
                disabled={deleting}
                aria-label={`Delete ${label}`}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
        )}
      </div>

      {onDelete && (
        <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete &ldquo;{label}&rdquo;?</AlertDialogTitle>
              <AlertDialogDescription>
                Soft-deletes this asset from the library. Already-published manifests are unaffected
                and keep serving it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  setConfirmingDelete(false);
                  onDelete();
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
